import React, {useEffect, useReducer} from 'react';
import {Box} from '@mui/material';
import {useIntl} from 'react-intl';
import {Lx} from '../../../../../../i18n/consts';
import Selector from '../../../../../../components/shared/nextgen/selector';
import {FilterActionType, FilterChangeAction, FilterState} from '../filter/types';
import {REmployees} from '../../../../../../features/roles/types';
import styles from './mobile-filter-sheet.module.scss';
import {initialVacationStates} from '../../../../../../features/vacation/reportSlice';
import SearchInput from '../../../../../../components/shared/search-input';
import { ActionCancelButtonsArea } from '../../../../../../components/shared/action-cancel-buttons-area';

interface Props {
    open: boolean;
    onClose: () => void;
    onApply: (filter: FilterState) => Promise<void> | void;
    onClear: () => void;
    search: string;
    onSearchChange: (value: string) => void;
    filters: FilterState;
    employees: REmployees[];
    disabled?: boolean;
    disableEmployeesFilter?: boolean;
}

interface ReducerState {
    draft: FilterState;
    current: FilterState;
}

const emptyFilterState: FilterState = {
    selectedEmployees: [],
    selectedVacationStates: []
};

function reducer(state: ReducerState, action: Partial<FilterChangeAction>): ReducerState {
    const nextState: ReducerState = {...state};

    const handlePropertyChange = (type: FilterActionType, payload: any): FilterState => {
        const nextDraft = {...nextState.draft};
        switch (type) {
            case 'EMPLOYEES_UPDATE':
                nextDraft.selectedEmployees = payload ?? [];
                break;
            case 'STATES_UPDATE':
                nextDraft.selectedVacationStates = payload ?? [];
                break;
        }
        return nextDraft;
    };

    switch (action.action) {
        case 'HYDRATE': {
            const incoming = (action.payload as FilterState) ?? emptyFilterState;
            nextState.current = {...incoming};
            nextState.draft = {...incoming};
            break;
        }
        case 'APPLY': {
            nextState.current = {...state.draft};
            break;
        }
        case 'RESET': {
            nextState.draft = {...emptyFilterState};
            break;
        }
        case 'CANCEL': {
            nextState.draft = {...state.current};
            break;
        }
        default: {
            nextState.draft = handlePropertyChange(action.action as FilterActionType, action.payload);
            break;
        }
    }

    return nextState;
}

const initialReducerState: ReducerState = {
    current: emptyFilterState,
    draft: emptyFilterState,
};

const VacationMobileFilterSheet = ({
    open,
    onClose,
    onApply,
    onClear,
    search,
    onSearchChange,
    filters,
    employees,
    disabled,
    disableEmployeesFilter,
}: Props) => {
    const intl = useIntl();
    const [filterState, dispatch] = useReducer(reducer, initialReducerState);

    useEffect(() => {
        if (open) {
            dispatch({ action: 'HYDRATE', payload: filters });
        } else {
            dispatch({ action: 'CANCEL' });
        }
    }, [open, filters]);

    const handleApply = () => {
        dispatch({ action: 'APPLY' });
        onApply(filterState.draft);
        onClose();
    };

    const handleClearFilters = () => {
        dispatch({ action: 'RESET' });
        onClear();
    };

    if (!open || disabled) {
        return null;
    }

    return (
        <div className={styles.sheet}>
            <div className={styles.content}>
                <SearchInput
                    searchValue={search ?? ''}
                    onChange={onSearchChange}
                    width='100%'
                    disabled={disabled}
                />

                {!disableEmployeesFilter && (
                    <div className={styles.selectorBlock}>
                        <Selector
                            id={'vacation-mobile-filter-employees'}
                            label={Lx.General.EMPLOYEES}
                            items={employees.map((employee) => ({ id: employee.id, name: employee.name, originalId: employee.id }))}
                            name={Lx.General.EMPLOYEES}
                            selected={filterState.draft.selectedEmployees}
                            setSelectedValues={(values) => dispatch({ action: 'EMPLOYEES_UPDATE', payload: values })}
                            intl={intl}
                            maxWidth={1000}
                            disabled={disabled}
                        />
                    </div>
                )}

                <div className={styles.selectorBlock}>
                    <Selector
                        id={'vacation-mobile-filter-states'}
                        label={Lx.Vacation.STATE}
                        items={initialVacationStates.map((state) => ({ ...state, name: intl.formatMessage({ id: state.name }) }))}
                        name={Lx.Vacation.STATE}
                        selected={filterState.draft.selectedVacationStates}
                        setSelectedValues={(values) => dispatch({ action: 'STATES_UPDATE', payload: values })}
                        intl={intl}
                        maxWidth={1000}
                        disabled={disabled}
                    />
                </div>
            </div>
            <Box className={styles.footer}>
                <ActionCancelButtonsArea 
                    idActionLocalization={Lx.General.APPLY} 
                    idCancelLocalization={Lx.General.RESET_FILTER} 
                    onCancel={handleClearFilters} 
                    onAction={handleApply}
                    disabled={disabled} />
            </Box>
        </div>
    );
};

export default VacationMobileFilterSheet;
